/**
 * Email hub URL base-path config.
 *
 * SAM mounts the email feature at `/email` (e.g. /email/inbox,
 * /email/compose), so all internal Link/navigate calls historically
 * hard-coded `/email/*`. The standalone hub at email.quotech.ai
 * mounts the feature at `/` (e.g. /inbox, /compose, /<id>) — the
 * hard-coded prefix breaks every nav link there (Compose / Email
 * View / Back-to-Inbox after send / etc.).
 *
 * Host injects its mount path once on bootstrap via
 * `setEmailBasePath('')` (apps/email) or leaves the default
 * `/email` (apps/sam). All package nav reads `emailPath()` so
 * routes stay correct on either host.
 */
let _basePath = '/email'

/**
 * Set the URL prefix the email feature is mounted under in the
 * host's React Router config.
 *
 * Defaults to `/email` for back-compat with apps/sam (where the
 * feature lives at /email/*). apps/email (email.quotech.ai)
 * mounts at `/` and calls `setEmailBasePath('')` on boot.
 *
 * Pass an empty string for "mounted at root" (no prefix).
 * Pass a leading-slash path with no trailing slash, e.g. '/email'.
 */
export function setEmailBasePath(basePath: string) {
  // Normalize: strip trailing slash (callers may pass '/email/' by
  // accident; emailPath('inbox') would then double up to
  // '/email//inbox').
  _basePath = basePath.replace(/\/+$/, '')
}

/**
 * Returns a fully-qualified path inside the email feature, prefixed
 * with the configured base path. Use this for every Link/navigate
 * call inside the package so the URLs stay correct on whichever
 * host mounted the feature.
 *
 *   emailPath('/inbox')  // SAM:  '/email/inbox'
 *   emailPath('/inbox')  // hub:  '/inbox'
 *   emailPath('inbox')   // accepts unleading-slash too
 *   emailPath('')        // returns '' on hub, '/email' on SAM
 */
export function emailPath(path: string): string {
  if (!path) return _basePath || '/'
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${_basePath}${normalized}`
}

/**
 * Read the current base-path. Used by tests and by the package's
 * own diagnostics; host apps should not need this.
 */
export function getEmailBasePath(): string {
  return _basePath
}
